import { Booking } from '@app/domain/Booking'
import { BookingStatusType } from '@app/domain/BookingStatus'
import { Charge } from '@app/domain/Charge'
import { DepositStatus } from '@app/domain/Deposit'
import { ServiceStatus } from '@app/domain/ServiceStatus'
import { Venue } from '@app/domain/Venue'

export interface BookingViewModelDelegate {
    markAsRunningLate(): void
    setPartyToWaiting(): void
    partiallySeatBooking(): void
    seatBooking(): void
    unseatBooking(): void
    finishBooking(): void
    reseatBooking(): void
    updateServiceStatus(serviceStatus: ServiceStatus | null): void
    undoCancellation(): void
}

export class BookingViewModel {

    canAcceptBooking: boolean
    canRejectBooking: boolean
    canRejectAndRefundBooking: boolean
    canCancelBooking: boolean
    canNoShowBooking: boolean
    canPartiallySeatParty: boolean
    canPartiallyChargeBooking: boolean
    canSetPartyToWaiting: boolean
    canSeatParty: boolean
    canUnseatParty: boolean
    canFinishBooking: boolean
    canReseatParty: boolean
    canVoidBooking: boolean
    canUndoCancellation: boolean
    cancellationCharge: Charge | null
    showCancelBookingButton: boolean
    showCancelAndChargeButton: boolean
    showCancelAndRefundDepositButton: boolean
    showNoShowBookingButton: boolean
    showNoShowAndChargeButton: boolean
    showNoShowAndRefundDepositButton: boolean
    showMarkAsRunningLateButton: boolean
    canRefundDeposit: boolean
    hasDepositRefundCutOffPassed: boolean
    enabledServiceStatuses: ServiceStatus[]
    canUpdateServiceStatus: boolean
    currentServiceStatus: ServiceStatus | null

    constructor(
        private booking: Booking,
        private venue: Venue,
        private delegate: BookingViewModelDelegate
    ) {
        const isFutureBooking = this.isBookingInFuture()
        
        this.canAcceptBooking = booking.status().canAcceptBooking()
        this.canRejectAndRefundBooking = booking.status().canRejectBooking() && this.booking.deposit?.status === DepositStatus.Paid
        this.canRejectBooking = booking.status().canRejectBooking() && !this.canRejectAndRefundBooking
        this.canCancelBooking = booking.status().canCancelBooking()
        this.enabledServiceStatuses = venue.selectableEnabledServiceStatuses
        this.canUpdateServiceStatus = booking.status().type === BookingStatusType.Seated && this.enabledServiceStatuses.length > 0
        this.currentServiceStatus = this.canUpdateServiceStatus && venue.serviceStatusIsEnabled(booking.serviceStatus)
            ? booking.serviceStatus
            : null
        const hasActiveServiceStatus = this.currentServiceStatus !== null
        this.canNoShowBooking = booking.status().canNoShowBooking() && !isFutureBooking
        this.canSetPartyToWaiting = booking.status().canSetPartyToWaiting() && !isFutureBooking && !hasActiveServiceStatus
        this.canPartiallySeatParty = booking.status().canPartiallySeatParty() && !isFutureBooking
        this.canSeatParty = booking.status().canSeatParty() && !isFutureBooking
        this.canUnseatParty = booking.status().canUnseatParty() && !hasActiveServiceStatus
        this.canFinishBooking = booking.status().canFinishBooking()
        this.canReseatParty = booking.status().canReseatParty()
        this.canVoidBooking = booking.status().canVoidBooking()
        this.canUndoCancellation = booking.status().canUndoCancellation()
        this.showMarkAsRunningLateButton = booking.status().canBeMarkedAsRunningLate() && !isFutureBooking

        this.cancellationCharge = this.booking.activeCancellationChargeForBooking()
        this.canRefundDeposit = this.booking.deposit?.status === DepositStatus.Paid
        this.hasDepositRefundCutOffPassed = this.venue.hasDepositRefundCutOffPassed(this.booking)
        // Can partially charge if booking is active (Booked status)
        // and has a per-cover cancellation charge
        // and there is more than 1 cover left to charge
        this.canPartiallyChargeBooking = booking.status().canSeatParty()
            && this.cancellationCharge !== null
            && this.cancellationCharge.isPerCover
            && (this.booking.cancellation?.remainingCoversToCharge(booking.size) ?? booking.size) > 1
            && !isFutureBooking
        this.showCancelBookingButton = this.canCancelBooking
            && !this.cancellationCharge
            && !this.canRefundDeposit
        this.showCancelAndChargeButton = this.canCancelBooking
            && this.cancellationCharge !== null
            && !this.canRefundDeposit
        this.showCancelAndRefundDepositButton = this.canCancelBooking
            && !this.cancellationCharge
            && this.canRefundDeposit
        this.showNoShowBookingButton = this.canNoShowBooking
            && !this.cancellationCharge
            && !this.canRefundDeposit
        this.showNoShowAndChargeButton = this.canNoShowBooking
            && this.cancellationCharge !== null
            && !this.canRefundDeposit
        this.showNoShowAndRefundDepositButton = this.canNoShowBooking
            && !this.cancellationCharge
            && this.canRefundDeposit
    }

    markAsRunningLate() {
        this.delegate.markAsRunningLate()
    }

    setPartyToWaiting() {
        this.delegate.setPartyToWaiting()
    }

    partiallySeatBooking() {
        this.delegate.partiallySeatBooking()
    }

    seatBooking() {
        this.delegate.seatBooking()
    }

    unseatBooking() {
        this.delegate.unseatBooking()
    }

    finishBooking() {
        this.delegate.finishBooking()
    }

    reseatBooking() {
        this.delegate.reseatBooking()
    }

    updateServiceStatus(serviceStatus: ServiceStatus | null) {
        this.delegate.updateServiceStatus(serviceStatus)
    }

    undoCancellation() {
        this.delegate.undoCancellation()
    }

    private isBookingInFuture(): boolean {
        const now = new Date()
        const hoursInFuture = 24
        const futureThreshold = new Date(now.getTime() + (hoursInFuture * 60 * 60 * 1000))
        return this.booking.start > futureThreshold
    }
}
